/* import __COLOCATED_TEMPLATE__ from './escalation.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import type Settings from 'embercom/models/ai-agent/tone-of-voice-settings';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type EscalationRule from 'embercom/models/ai-agent/escalation-rule';
import { MAX_GUIDELINE_COUNT } from 'embercom/components/ai-agent/guidance';

const SURVEY_ID = 44017774;
enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

interface Args {
  openSectionId?: string;
}
export default class AiAgentEscalation extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;

  @tracked openSectionId = '';
  @tracked personalityCustomizationSettings?: Settings;
  @tracked previewLocale = 'en';
  @tracked showPreview = true;

  MAX_GUIDELINE_COUNT = MAX_GUIDELINE_COUNT;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.getPersonalityCustomizationSettings();

    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }

    if (args.openSectionId) {
      this.openSectionId = args.openSectionId;
    }
  }

  get routingGuidanceChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.guidelines.forEach((guideline) => {
      hash[guideline.id] = guideline.chartDefinitions;
    });
    return hash;
  }

  get escalationRuleChartDefinitionsHash(): Record<string, Record<Stat, any>> {
    let hash: Record<string, Record<Stat, any>> = {};
    this.escalationRules.forEach((escalationRule) => {
      hash[escalationRule.id] = escalationRule.chartDefinitions;
    });
    return hash;
  }

  async getPersonalityCustomizationSettings(): Promise<$TSFixMe> {
    this.personalityCustomizationSettings = await this.store.findRecord(
      'ai-agent/tone-of-voice-settings',
      this.appService.app.id,
    );
  }

  get escalationRules(): EscalationRule[] {
    return this.store.peekAll('ai-agent/escalation-rule').toArray();
  }

  get guidelines(): Guideline[] {
    return this.store
      .peekAll('ai-agent/guidelines')
      .filterBy('category', 'routing')
      .rejectBy('isDeleted')
      .toArray();
  }

  get hasInvalidGuidelines(): boolean {
    return this.guidelines.some((guideline) => guideline.validations.isInvalid);
  }

  get validationErrors(): string[] {
    return this.guidelines
      .filter((guideline) => guideline.validations.isInvalid)
      .map((guideline) => guideline.validations.messages)
      .flat()
      .uniq();
  }

  get hasUnsavedChanges(): boolean {
    return this.guidelines.some((guideline) => guideline.hasDirtyAttributes || guideline.isDeleted);
  }

  get disableSaveButton(): boolean {
    return this.hasInvalidGuidelines || !this.hasUnsavedChanges;
  }

  get hasContentReadyForFin() {
    return this.aiAgentSetupService.hasContentReadyForFin;
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  async _saveChangedGuidelines(): Promise<void> {
    // .save() by itself is sending requests for all guidelines
    let unsavedGuidelines = this.guidelines.filter((guideline) => guideline.hasDirtyAttributes);

    for (let guideline of unsavedGuidelines) {
      // to ensure new guidelines are created in order
      await guideline.save();
    }
  }

  @dropTask
  *save(): TaskGenerator<void> {
    try {
      yield this._saveChangedGuidelines();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
    }
  }

  @action
  sendPreviewSettingsUpdatedMessage() {
    this.aiAgentGuidelineService.sendPreviewSettingsUpdatedMessage();
  }

  get userIsTyping(): boolean {
    return !!this.aiAgentGuidelineService.typingTimeout;
  }

  willDestroy() {
    // We need to clear the timeout in case the user navigates away from the page while typing
    // otherwise the message will be sent to the iframe even after the page has been unloaded
    super.willDestroy();
    if (this.aiAgentGuidelineService.typingTimeout) {
      clearTimeout(this.aiAgentGuidelineService.typingTimeout);
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  @action
  togglePreview() {
    this.showPreview = !this.showPreview;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Escalation': typeof AiAgentEscalation;
  }
}
